import React, { useEffect } from 'react';
import { Layer, Source, WebMercatorViewport } from 'react-map-gl';
import { SheetVersion } from 'records/SheetVersion';
import { calculateCornerCoordinates } from 'utils/sheetCalibration';
import { useCalibrationMachine } from './CalibrationMachine';

type SheetImageSourceProps = {
  mapContainerId: string;
  sheetVersion: SheetVersion;
  onFitImageViewportCalculated: (fitImageViewport: Viewport) => void;
};

const SheetImageSource: React.FC<SheetImageSourceProps> = ({
  mapContainerId,
  sheetVersion,
  onFitImageViewportCalculated,
}: SheetImageSourceProps) => {
  const { calibrationMachineState } = useCalibrationMachine();
  const { pageUrl } = calibrationMachineState.context;

  const imageCorners = calculateCornerCoordinates({
    center: {
      latitude: 0,
      longitude: 0,
    },
    pdfWidth: sheetVersion.width,
    pdfHeight: sheetVersion.height,
    realFeetPerPaperInch: 200,
  });

  const { topleft, topright, bottomright, bottomleft } = imageCorners;

  const imageSourceCorners = [
    [topleft.longitude, topleft.latitude],
    [topright.longitude, topright.latitude],
    [bottomright.longitude, bottomright.latitude],
    [bottomleft.longitude, bottomleft.latitude],
  ];

  const getFitImageViewport = () => {
    const mapContainer = document.getElementById(mapContainerId);

    if (!mapContainer) {
      return null;
    }

    const fitImageViewport = new WebMercatorViewport({
      width: mapContainer.offsetWidth,
      height: mapContainer.offsetHeight,
    }).fitBounds(
      [
        [bottomleft.longitude, bottomleft.latitude],
        [topright.longitude, topright.latitude],
      ],
      {
        padding: 24,
      }
    );

    return fitImageViewport;
  };

  useEffect(() => {
    const fitImageViewport = getFitImageViewport();
    if (fitImageViewport) {
      onFitImageViewportCalculated(fitImageViewport);
    }
  }, []);

  return pageUrl ? (
    <>
      <Source
        type="geojson"
        data={{
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[...imageSourceCorners, imageSourceCorners[0]]],
          },
          properties: {},
        }}
      >
        <Layer
          id="sheet-image-background-layer"
          type="fill"
          paint={{ 'fill-color': '#ffffff' }}
        />
      </Source>
      <Source type="image" coordinates={imageSourceCorners} url={pageUrl}>
        <Layer id="sheet-image-layer" type="raster" paint={{}} />
      </Source>
    </>
  ) : null;
};

export default SheetImageSource;
